import React, { useMemo } from 'react';
import './style.scss';
import clsx from 'clsx';
import Link from '../link';
import Loader from '../../general/loader';
import { LinkProp } from '../../../interfaces/LinkProp';
import { useAssets } from '../../../contexts/assets';

interface ButtonProps {
  children?: React.ReactNode;
  onClick?: (...args: any[]) => any;
  className?: string;
  variant?: 'contained' | 'outlined';
  color?: 'primary' | 'secondary' | 'default' | 'black';
  disabled?: boolean;
  loading?: boolean;
  pixelFont?: boolean;
  link?: LinkProp;
  noPadding?: boolean;
  fullWidth?: boolean;
  style?: any;
  minified?: boolean;
  noSound?: boolean;
}

const Button = ({
  children,
  onClick,
  className = '',
  variant = 'contained',
  color = 'primary',
  pixelFont = false,
  disabled = false,
  loading = false,
  link,
  noPadding = false,
  fullWidth = false,
  minified = false,
  style = {},
  noSound = false
}: ButtonProps) => {
  const { sound } = useAssets();
  const finalClassName = useMemo(() => {
    return clsx(
      'button',
      `button-${variant}`,
      `button-${color}`,
      noPadding && 'button-no-padding',
      fullWidth && 'button-full-width',
      disabled && 'button-disabled',
      minified && 'button-minified'
    );
  }, [className, variant, color, noPadding, disabled, fullWidth]);

  const handleClick = (e: any) => {
    if (onClick) {
      onClick(e);
      if (!noSound) {
        sound.click();
      }
    }
  };

  if (link && !disabled && !loading)
    return (
      <Link to={link} className={className} style={style}>
        <ButtonBody
          className={finalClassName}
          disabled={disabled}
          onClick={handleClick}
          loading={loading}
          pixel={pixelFont}
        >
          {children}
        </ButtonBody>
      </Link>
    );

  return (
    <ButtonBody
      disabled={disabled}
      loading={loading}
      className={clsx(finalClassName, className)}
      style={style}
      onClick={handleClick}
      pixel={pixelFont}
    >
      {children}
    </ButtonBody>
  );
};

interface ButtonBodyProps {
  disabled: boolean;
  loading: boolean;
  onClick?: (...args: any[]) => any;
  className: string;
  pixel: boolean;
  style?: any;
  children: React.ReactNode;
}

const ButtonBody = ({
  disabled,
  loading,
  onClick,
  className,
  style,
  pixel,
  children
}: ButtonBodyProps) => (
  <button
    onClick={disabled || loading ? undefined : onClick}
    className={className}
    disabled={disabled}
    style={style}
  >
    <div
      className={clsx(
        'button-content',
        loading && 'button-content-hidden',
        pixel && 'button-content-pixel'
      )}
    >
      {children}
    </div>
    {loading && <Loader color='white' size={35} useAbsoluteWrapper />}
  </button>
);

export default Button;
